import React, { useState, useEffect } from 'react';
import { api } from '../../context/AppContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Play, X, Heart, ChevronDown, ChevronUp, Calendar, Target, Zap, ArrowLeft, CheckCircle2, Clock, Repeat, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';
import { DIFFICULTY_CONFIG } from './constants';

export function ProgramCard({ program, language, onSelect }) {
  const diff = DIFFICULTY_CONFIG[program.difficulty] || DIFFICULTY_CONFIG.intermediate;
  const totalExercises = program.days?.reduce((acc, d) => acc + (d.exercises?.length || 0), 0) || '-';

  return (
    <Card
      className="bg-card border-border hover:border-primary/40 transition-all cursor-pointer group overflow-hidden"
      onClick={() => onSelect(program.program_id)}
      data-testid={`program-${program.program_id}`}
    >
      <div className="h-2 bg-gradient-to-r from-primary/60 to-primary/20" />
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading text-base font-bold uppercase tracking-tight text-foreground leading-tight">
            {language === 'fr' ? program.name_fr : program.name_en}
          </h3>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold shrink-0 ${diff.color}`}>
            {language === 'fr' ? diff.label_fr : diff.label_en}
          </span>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
          {language === 'fr' ? program.description_fr : program.description_en}
        </p>

        <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-1">
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-primary/60" />
            {program.duration_weeks} {language === 'fr' ? 'sem.' : 'wks'}
          </span>
          <span className="flex items-center gap-1">
            <Target className="w-3.5 h-3.5 text-primary/60" />
            {program.days_per_week}x / {language === 'fr' ? 'sem.' : 'wk'}
          </span>
          <span className="flex items-center gap-1">
            <Zap className="w-3.5 h-3.5 text-primary/60" />
            {language === 'fr' ? program.goal_fr : program.goal_en}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}


export function ProgramDetail({ programId, language, onBack, favorites, onToggleFav, onFullscreen }) {
  const [program, setProgram] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [progRes, progResProgress] = await Promise.all([
          api.get(`/workouts/programs/${programId}`),
          api.get(`/workouts/programs/${programId}/user-progress`),
        ]);
        setProgram(progRes.data);
        setProgress(progResProgress.data);
        if (progRes.data?.days?.length > 0) {
          setExpandedDay(progRes.data.days[0].day_number);
        }
      } catch (e) { console.error(e); toast.error('Erreur de chargement'); }
      finally { setLoading(false); }
    };
    load();
  }, [programId]);

  const startProgram = async () => {
    try {
      await api.post(`/workouts/programs/${programId}/start`);
      setProgress({ started: true, completed_days: [], current_week: 1 });
      toast.success(language === 'fr' ? 'Programme commencé !' : 'Program started!');
    } catch (e) { toast.error('Erreur'); }
  };

  const completeDay = async (dayNumber) => {
    try {
      const res = await api.post(`/workouts/programs/${programId}/complete-day`, { day_number: dayNumber });
      setProgress(res.data);
      toast.success(language === 'fr' ? 'Jour complété !' : 'Day completed!');
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!program) return null;

  const diff = DIFFICULTY_CONFIG[program.difficulty] || DIFFICULTY_CONFIG.intermediate;
  const completedDays = progress?.completed_days || [];
  const totalDays = program.days?.length || 0;
  const progressPct = totalDays > 0 ? Math.round((completedDays.length / totalDays) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="program-detail">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-programs">
        <ArrowLeft className="w-4 h-4" /> {language === 'fr' ? 'Retour aux programmes' : 'Back to programs'}
      </button>

      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-foreground">
            {language === 'fr' ? program.name_fr : program.name_en}
          </h2>
          <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold shrink-0 ${diff.color}`}>
            {language === 'fr' ? diff.label_fr : diff.label_en}
          </span>
        </div>

        <p className="text-sm text-muted-foreground">
          {language === 'fr' ? program.description_fr : program.description_en}
        </p>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {program.duration_weeks} {language === 'fr' ? 'semaines' : 'weeks'}</span>
          <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" /> {program.days_per_week}x / {language === 'fr' ? 'semaine' : 'week'}</span>
          <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> {language === 'fr' ? program.goal_fr : program.goal_en}</span>
        </div>

        {progress?.started && (
          <div className="space-y-1.5" data-testid="program-progress">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{completedDays.length}/{totalDays} {language === 'fr' ? 'jours complétés' : 'days completed'}</span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        {!progress?.started && (
          <Button onClick={startProgram} className="w-full sm:w-auto" data-testid="start-program-btn">
            <Zap className="w-4 h-4 mr-2" /> {language === 'fr' ? 'Commencer ce programme' : 'Start this program'}
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {program.days?.map(day => {
          const isExpanded = expandedDay === day.day_number;
          const isCompleted = completedDays.includes(day.day_number);

          return (
            <Card key={day.day_number} className={`border-border overflow-hidden ${isCompleted ? 'border-green-500/30 bg-green-500/5' : 'bg-card'}`} data-testid={`program-day-${day.day_number}`}>
              <button
                onClick={() => setExpandedDay(isExpanded ? null : day.day_number)}
                className="w-full p-3 flex items-center justify-between hover:bg-secondary/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isCompleted ? 'bg-green-500/20 text-green-400' : 'bg-primary/10 text-primary'}`}>
                    {isCompleted ? <CheckCircle2 className="w-4 h-4" /> : day.day_number}
                  </div>
                  <div className="text-left">
                    <h4 className="text-sm font-semibold text-foreground">
                      {language === 'fr' ? day.name_fr : day.name_en}
                    </h4>
                    <p className="text-[11px] text-muted-foreground">{day.exercises?.length} exercices</p>
                  </div>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>

              {isExpanded && (
                <div className="border-t border-border">
                  <div className="divide-y divide-border/50">
                    {day.exercises?.map((ex, i) => {
                      const exData = ex.exercise_data || {};
                      const hasVid = !!exData.video_url;
                      const isPlaying = playingVideo === `${day.day_number}-${i}`;
                      const isFav = favorites.includes(ex.exercise_id);

                      return (
                        <div key={i} className="p-3 space-y-2">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => hasVid && setPlayingVideo(isPlaying ? null : `${day.day_number}-${i}`)}
                              className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${hasVid ? 'bg-primary/15 hover:bg-primary/25 cursor-pointer' : 'bg-secondary'}`}
                              data-testid={`play-ex-${day.day_number}-${i}`}
                            >
                              {hasVid ? <Play className="w-4 h-4 text-primary" /> : <span className="text-xs text-muted-foreground font-bold">{i + 1}</span>}
                            </button>

                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {language === 'fr' ? (exData.name_fr || ex.exercise_name) : (exData.name_en || ex.exercise_name)}
                              </p>
                              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                                <span className="flex items-center gap-0.5"><Repeat className="w-3 h-3" /> {ex.sets}x{ex.reps}</span>
                                <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {ex.rest}s</span>
                                {exData.muscle_groups?.[0] && <span className="text-primary/70">{exData.muscle_groups[0]}</span>}
                              </div>
                            </div>

                            {ex.exercise_id && (
                              <button onClick={() => onToggleFav(ex.exercise_id)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-secondary transition-colors" data-testid={`prog-fav-${ex.exercise_id}`}>
                                <Heart className={`w-4 h-4 ${isFav ? 'text-red-400 fill-red-400' : 'text-muted-foreground/40'}`} />
                              </button>
                            )}
                          </div>

                          {isPlaying && hasVid && (
                            <div className="relative bg-zinc-900 rounded-lg overflow-hidden ring-1 ring-white/10" style={{ aspectRatio: '16 / 10' }}>
                              <iframe
                                src={exData.video_url}
                                className="absolute inset-0 w-full h-full border-0"
                                style={{ background: '#fff' }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                title={exData.name_en}
                              />
                              <button
                                onClick={() => setPlayingVideo(null)}
                                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/80 ring-1 ring-white/20 flex items-center justify-center text-white hover:bg-red-600/90 hover:ring-red-400/40 transition-all z-10 shadow-lg"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => onFullscreen && onFullscreen({ url: exData.video_url, name: exData.name_fr || ex.exercise_name })}
                                className="absolute top-2 right-12 w-8 h-8 rounded-full bg-black/80 ring-1 ring-white/20 flex items-center justify-center text-white hover:bg-primary/80 hover:ring-primary/40 transition-all z-10 shadow-lg"
                                data-testid="fullscreen-prog-video-btn"
                              >
                                <Maximize2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {progress?.started && !isCompleted && (
                    <div className="p-3 border-t border-border">
                      <Button
                        onClick={() => completeDay(day.day_number)}
                        className="w-full"
                        size="sm"
                        data-testid={`complete-day-${day.day_number}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                        {language === 'fr' ? 'Marquer comme complété' : 'Mark as completed'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
